// GENERATED FILE. DO NOT EDIT.
//
// The selectable field tree of each Data API operation, generated from the Data API
// operation catalog the Mountaya platform builds from the SDL, not from the SDL itself.
// The catalog carries the depth-bounded tree the server prints each operation's full
// document from, which is what this file has to agree with.
//
// A leaf is `true`; a composite is a nested tree. This is the shape `select` is checked
// against, so it is also the definition of what counts as a leaf: the GeoJSON scalar is
// an object on the wire and a leaf here, which a tree derived from the result type could
// not express.
//
// The SDL is authored in mountayaapp/schemas (graphql/data); edit it there, not here.

import type { Selection } from "../core/rules/selection.js";

/** The selectable fields of `analyzeGeometry`. */
export const analyzeGeometryFields = {
  analysis: {
    elevation: {
      altitude: { max: true, min: true },
      ascent: true,
      descent: true,
      gradient: { max: true, min: true },
    },
    elevations: true,
    slopeInfo: { fromIndex: true, toIndex: true, value: true },
    speedInfo: { fromIndex: true, toIndex: true, value: true },
    surfaceInfo: { fromIndex: true, toIndex: true, value: true },
    trailDifficultyInfo: { fromIndex: true, toIndex: true, value: true },
    wayTypeInfo: { fromIndex: true, toIndex: true, value: true },
    advisories: {
      tollway: true,
      restrictedAccess: true,
      tollwayInfo: { fromIndex: true, toIndex: true, value: true },
      roadAccessInfo: { fromIndex: true, toIndex: true, values: true },
    },
  },
  duration: true,
} as const;

/** A `select` for `analyzeGeometry`. */
export type AnalyzeGeometrySelection = Selection<typeof analyzeGeometryFields>;

/** The selectable fields of `directions`. */
export const directionsFields = {
  routes: {
    distance: true,
    duration: true,
    elevation: {
      altitude: { max: true, min: true },
      ascent: true,
      descent: true,
      gradient: { max: true, min: true },
    },
    geometry: true,
    analysis: {
      elevation: {
        altitude: { max: true, min: true },
        ascent: true,
        descent: true,
        gradient: { max: true, min: true },
      },
      elevations: true,
      slopeInfo: { fromIndex: true, toIndex: true, value: true },
      speedInfo: { fromIndex: true, toIndex: true, value: true },
      surfaceInfo: { fromIndex: true, toIndex: true, value: true },
      trailDifficultyInfo: { fromIndex: true, toIndex: true, value: true },
      wayTypeInfo: { fromIndex: true, toIndex: true, value: true },
      advisories: {
        tollway: true,
        restrictedAccess: true,
        tollwayInfo: { fromIndex: true, toIndex: true, value: true },
        roadAccessInfo: { fromIndex: true, toIndex: true, values: true },
      },
    },
    avoidance: true,
    summary: true,
    steps: {
      fromIndex: true,
      toIndex: true,
      type: true,
      name: true,
      distance: true,
      duration: true,
      exitNumber: true,
    },
  },
} as const;

/** A `select` for `directions`. */
export type DirectionsSelection = Selection<typeof directionsFields>;

/** The selectable fields of `exportItinerary`. */
export const exportItineraryFields = {
  url: true,
  filename: true,
  format: true,
  expiresAt: true,
} as const;

/** A `select` for `exportItinerary`. */
export type ExportItinerarySelection = Selection<typeof exportItineraryFields>;

/** The selectable fields of `geocode`. */
export const geocodeFields = {
  count: true,
  places: {
    osm: { id: true, type: true, key: true, value: true },
    name: true,
    label: true,
    layer: true,
    category: true,
    point: true,
    bounds: true,
    country: true,
    countryName: true,
    region: true,
    county: true,
    city: true,
    postcode: true,
    street: true,
    houseNumber: true,
  },
} as const;

/** A `select` for `geocode`. */
export type GeocodeSelection = Selection<typeof geocodeFields>;

/** The selectable fields of `isochrones`. */
export const isochronesFields = {
  isochrones: { value: true, area: true, reachFactor: true, center: true, geometry: true },
} as const;

/** A `select` for `isochrones`. */
export type IsochronesSelection = Selection<typeof isochronesFields>;

/** The selectable fields of `itinerary`. */
export const itineraryFields = {
  id: true,
  name: true,
  description: true,
  activitySlug: true,
  distance: true,
  ascent: true,
  isPrivate: true,
  organizationId: true,
  segments: {
    id: true,
    name: true,
    description: true,
    position: true,
    groupId: true,
    distance: true,
    duration: true,
    ascent: true,
    descent: true,
    altitude: { max: true, min: true },
    gradient: { max: true, min: true },
    geometry: true,
  },
  waypoints: {
    id: true,
    name: true,
    description: true,
    position: true,
    point: true,
    amenities: true,
    timing: { cutoffAt: true, stopDuration: true },
    contact: {
      website: true,
      phone: true,
      email: true,
      address: true,
      openingHours: true,
      bookingUrl: true,
    },
    media: { photos: { url: true, caption: true }, links: { label: true, url: true } },
    properties: { key: true, label: true, value: true, format: true },
  },
  groups: { id: true, name: true, color: true, speed: true },
  segmentCount: true,
  waypointCount: true,
  groupCount: true,
} as const;

/** A `select` for `itinerary`. */
export type ItinerarySelection = Selection<typeof itineraryFields>;

/** The selectable fields of `matrix`. */
export const matrixFields = { durations: true, distances: true } as const;

/** A `select` for `matrix`. */
export type MatrixSelection = Selection<typeof matrixFields>;

/** The selectable fields of `pois`. */
export const poisFields = {
  count: true,
  pois: {
    osm: { id: true, type: true, key: true, value: true },
    name: true,
    category: true,
    point: true,
    elevation: true,
    tags: { key: true, value: true },
    distanceMeters: true,
    country: true,
    countryName: true,
    region: true,
    city: true,
  },
} as const;

/** A `select` for `pois`. */
export type PoisSelection = Selection<typeof poisFields>;

/** The selectable fields of `reverseGeocode`. */
export const reverseGeocodeFields = {
  point: true,
  places: {
    osm: { id: true, type: true, key: true, value: true },
    name: true,
    label: true,
    layer: true,
    category: true,
    point: true,
    bounds: true,
    country: true,
    countryName: true,
    region: true,
    county: true,
    city: true,
    postcode: true,
    street: true,
    houseNumber: true,
  },
  count: true,
} as const;

/** A `select` for `reverseGeocode`. */
export type ReverseGeocodeSelection = Selection<typeof reverseGeocodeFields>;

/** The selectable fields of `search`. */
export const searchFields = {
  query: true,
  language: true,
  targets: {
    places: true,
    pois: true,
    ways: true,
    itineraries: true,
    collections: true,
    zones: true,
  },
  places: {
    count: true,
    places: {
      osm: { id: true, type: true, key: true, value: true },
      name: true,
      label: true,
      layer: true,
      category: true,
      point: true,
      bounds: true,
      country: true,
      countryName: true,
      region: true,
      county: true,
      city: true,
      postcode: true,
      street: true,
      houseNumber: true,
    },
  },
  pois: {
    count: true,
    pois: {
      osm: { id: true, type: true, key: true, value: true },
      name: true,
      category: true,
      point: true,
      elevation: true,
      tags: { key: true, value: true },
      distanceMeters: true,
      country: true,
      countryName: true,
      region: true,
      city: true,
    },
  },
  ways: {
    count: true,
    ways: {
      osm: { id: true, type: true, key: true, value: true },
      name: true,
      highway: true,
      surface: true,
      sacScale: true,
      point: true,
      bounds: true,
      coordinates: true,
      activityCompatible: true,
      country: true,
      countryName: true,
      region: true,
      city: true,
    },
    activityApplied: true,
  },
  itineraries: {
    count: true,
    itineraries: {
      id: true,
      name: true,
      description: true,
      distance: true,
      ascent: true,
      activitySlug: true,
      start: true,
      bounds: true,
      isPrivate: true,
      organizationId: true,
      score: true,
      matchedField: true,
    },
  },
  collections: {
    count: true,
    collections: {
      id: true,
      name: true,
      description: true,
      count: { itineraries: true, zones: true },
      bounds: true,
      isPrivate: true,
      organizationId: true,
      score: true,
      matchedField: true,
    },
  },
  zones: {
    count: true,
    zones: {
      id: true,
      name: true,
      description: true,
      level: true,
      mode: true,
      point: true,
      bounds: true,
      isPrivate: true,
      organizationId: true,
      score: true,
      matchedField: true,
    },
  },
  errors: { target: true, code: true, message: true },
} as const;

/** A `select` for `search`. */
export type SearchSelection = Selection<typeof searchFields>;

/** The selectable fields of `suggestions`. */
export const suggestionsFields = {
  routes: {
    distance: true,
    duration: true,
    elevation: {
      altitude: { max: true, min: true },
      ascent: true,
      descent: true,
      gradient: { max: true, min: true },
    },
    geometry: true,
    analysis: {
      elevation: {
        altitude: { max: true, min: true },
        ascent: true,
        descent: true,
        gradient: { max: true, min: true },
      },
      elevations: true,
      slopeInfo: { fromIndex: true, toIndex: true, value: true },
      speedInfo: { fromIndex: true, toIndex: true, value: true },
      surfaceInfo: { fromIndex: true, toIndex: true, value: true },
      trailDifficultyInfo: { fromIndex: true, toIndex: true, value: true },
      wayTypeInfo: { fromIndex: true, toIndex: true, value: true },
      advisories: {
        tollway: true,
        restrictedAccess: true,
        tollwayInfo: { fromIndex: true, toIndex: true, value: true },
        roadAccessInfo: { fromIndex: true, toIndex: true, values: true },
      },
    },
    summary: true,
    steps: {
      fromIndex: true,
      toIndex: true,
      type: true,
      name: true,
      distance: true,
      duration: true,
      exitNumber: true,
    },
    seed: true,
  },
} as const;

/** A `select` for `suggestions`. */
export type SuggestionsSelection = Selection<typeof suggestionsFields>;

/** The selectable fields of `terrainGrid`. */
export const terrainGridFields = {
  count: true,
  coveredCount: true,
  zoom: true,
  resolutionMeters: true,
  cols: true,
  rows: true,
  cells: {
    point: true,
    elevation: true,
    slope: true,
    aspect: true,
    aspectOctant: true,
    steepnessBand: true,
    distanceMeters: true,
  },
} as const;

/** A `select` for `terrainGrid`. */
export type TerrainGridSelection = Selection<typeof terrainGridFields>;

/** The selectable fields of `terrainProfile`. */
export const terrainProfileFields = {
  count: true,
  coveredCount: true,
  zoom: true,
  resolutionMeters: true,
  samples: {
    point: true,
    elevation: true,
    slope: true,
    aspect: true,
    aspectOctant: true,
    steepnessBand: true,
    distanceMeters: true,
  },
} as const;

/** A `select` for `terrainProfile`. */
export type TerrainProfileSelection = Selection<typeof terrainProfileFields>;

/** The selectable fields of `terrainSamples`. */
export const terrainSamplesFields = {
  count: true,
  coveredCount: true,
  zoom: true,
  resolutionMeters: true,
  samples: {
    point: true,
    elevation: true,
    slope: true,
    aspect: true,
    aspectOctant: true,
    steepnessBand: true,
    distanceMeters: true,
  },
} as const;

/** A `select` for `terrainSamples`. */
export type TerrainSamplesSelection = Selection<typeof terrainSamplesFields>;
